import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { modalStack, picking, toast } from '../store.js';
import { del } from '../api.js';

export const html = htm.bind(h);

export function md(text) {
  return { __html: DOMPurify.sanitize(marked.parse(text || '', { breaks: true })) };
}

export function notify(msg, kind = 'ok') {
  toast.value = { msg, kind, t: Date.now() };
  setTimeout(() => { if (toast.value && Date.now() - toast.value.t > 3500) toast.value = null; }, 3800);
}

export function openModal(kind, props = {}) { modalStack.value = [...modalStack.value, { kind, props, key: Math.random() }]; }
export function closeModal() { modalStack.value = modalStack.value.slice(0, -1); }

export function Modal({ title, children, footer, wide, xwide, onClose }) {
  const hidden = !!picking.value;
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !picking.value) (onClose || closeModal)(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, []);
  return html`<div class="modal-back" style=${hidden ? 'display:none' : ''} onMouseDown=${(e) => { if (e.target === e.currentTarget) (onClose || closeModal)(); }}>
    <div class=${'modal' + (wide ? ' wide' : '') + (xwide ? ' xwide' : '')} role="dialog" aria-modal="true" aria-label=${title}>
      <header><h2>${title}</h2><button class="btn ghost" aria-label="Close" onClick=${onClose || closeModal}>✕</button></header>
      <div class="body">${children}</div>
      ${footer && html`<footer>${footer}</footer>`}
    </div></div>`;
}

export function Field({ label, children, hint }) {
  return html`<label class="field"><span>${label}</span>${children}${hint && html`<div class="tiny muted" style="margin-top:3px">${hint}</div>`}</label>`;
}

export function useForm(init) {
  const [v, setV] = useState(init);
  const bind = (k, cast) => ({
    value: v[k] ?? '',
    onInput: (e) => setV(s => ({ ...s, [k]: cast ? cast(e.target.value) : e.target.value })),
  });
  return [v, setV, bind];
}

export function ConfirmButton({ label = 'Delete', confirm = 'Really delete?', onConfirm, cls = 'btn danger sm' }) {
  const [ask, setAsk] = useState(false);
  if (!ask) return html`<button class=${cls} onClick=${() => setAsk(true)}>${label}</button>`;
  return html`<span class="row" style="margin:0;gap:6px"><span class="small">${confirm}</span>
    <button class="btn danger sm" onClick=${() => { setAsk(false); onConfirm(); }}>Yes</button>
    <button class="btn sm" onClick=${() => setAsk(false)}>No</button></span>`;
}

// Photos, scanned maps and documents attached to a record.  Pictures and PDFs open in the viewer (zoom, pan, pages);
// other files download.  owner = {type, id, reload} lets members take a file off this record.
const viewable = (m) => m.kind === 'pdf' || m.kind === 'video' || (m.kind === 'image' && (m.thumb || m.mime !== 'image/tiff'));

export function Gallery({ media, owner }) {
  const [open, setOpen] = useState(-1);
  const items = media.filter(viewable), docs = media.filter(m => !viewable(m));
  const remove = async (m) => {
    try { await del(`/api/media/${m.id}/links/${owner.type}/${owner.id}`); setOpen(-1); notify('Removed'); owner.reload?.(); }
    catch (e) { notify(e.message, 'err'); }
  };
  return html`
    ${items.length > 0 && html`<div class="gallery">${items.map((m, i) => html`
      <button onClick=${() => setOpen(i)} title=${m.caption || m.name}>
        ${m.thumb ? html`<img src=${m.thumb} alt=${m.caption || m.name} loading="lazy" />`
          : m.kind === 'image' ? html`<img src=${m.url} alt=${m.caption || m.name} loading="lazy" />`
          : html`<span class="ph">${m.kind === 'video' ? '▶' : '📄'}<span>${m.name}</span></span>`}
        ${m.kind === 'pdf' && html`<span class="badge">PDF${m.pages ? ` · ${m.pages} p` : ''}</span>`}</button>`)}</div>`}
    ${docs.map(m => html`<div class="doc"><span>📄</span><a href=${m.url} target="_blank" rel="noopener">${m.name}</a>
      <span class="tiny muted">${(m.size / 1048576).toFixed(1)} MB</span>
      ${owner && html`<span style="flex:1"></span><${ConfirmButton} label="Remove" confirm="Remove it?" cls="btn sm ghost" onConfirm=${() => remove(m)} />`}</div>`)}
    ${open >= 0 && createPortal(html`<${Lightbox} items=${items} index=${open} onClose=${() => setOpen(-1)} onRemove=${owner ? remove : null} />`, document.body)}`;
}

// Full-screen viewer: wheel or +/− to zoom, drag to pan, double-click for 1:1 / fit, ‹ › between files and pages.
export function Lightbox({ items, index, onClose, onRemove }) {
  const [i, setI] = useState(index), [page, setPage] = useState(1), [maxPage, setMaxPage] = useState(null);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 }), [hi, setHi] = useState(false), [base, setBase] = useState(null);
  const stage = useRef(), drag = useRef(null), pts = useRef(new Map());
  const m = items[i], pages = m.kind === 'pdf' ? (m.pages || maxPage) : 1;
  const reset = () => { setT({ s: 1, x: 0, y: 0 }); setHi(false); };
  const go = (d) => { setI(x => (x + d + items.length) % items.length); setPage(1); setMaxPage(null); setBase(null); reset(); };
  const turn = (d) => { const p = page + d; if (p < 1 || (pages && p > pages)) return false; setPage(p); setBase(null); reset(); return true; };
  const zoomAt = (f, cx, cy) => setT(o => {
    const s = Math.min(16, Math.max(1, o.s * f)); const k = s / o.s;
    if (s > 1.6) setHi(true);
    return s === 1 ? { s: 1, x: 0, y: 0 } : { s, x: cx - (cx - o.x) * k, y: cy - (cy - o.y) * k };
  });
  const centre = () => { const r = stage.current.getBoundingClientRect(); return [r.width / 2, r.height / 2]; };
  useEffect(() => {
    const k = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'PageDown') { if (!(m.kind === 'pdf' && turn(1)) && items.length > 1) go(1); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { if (!(m.kind === 'pdf' && turn(-1)) && items.length > 1) go(-1); }
      else if (e.key === '+' || e.key === '=') zoomAt(1.4, ...centre());
      else if (e.key === '-') zoomAt(1 / 1.4, ...centre());
      else if (e.key === '0') reset();
    };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  });
  // size the picture to fit the stage; zoom and pan are a transform on top
  const fit = (img) => {
    const r = stage.current.getBoundingClientRect(), a = img.naturalWidth / img.naturalHeight;
    const w = Math.min(r.width, r.height * a); setBase({ w, h: w / a, left: (r.width - w) / 2, top: (r.height - w / a) / 2 });
  };
  const src = m.kind === 'pdf' ? `/api/media/${m.id}/page/${page}.png?w=${hi ? 3200 : 1600}` : (m.view || m.url);
  const lowSrc = m.kind === 'pdf' ? `/api/media/${m.id}/page/${page}.png?w=1600` : src;
  const onWheel = (e) => { e.preventDefault(); const r = stage.current.getBoundingClientRect(); zoomAt(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX - r.left, e.clientY - r.top); };
  const down = (e) => { pts.current.set(e.pointerId, [e.clientX, e.clientY]); e.currentTarget.setPointerCapture(e.pointerId); drag.current = { x: e.clientX, y: e.clientY, d: null }; };
  const move = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    const prev = pts.current.get(e.pointerId); pts.current.set(e.pointerId, [e.clientX, e.clientY]);
    if (pts.current.size === 2) {                          // pinch
      const [a, b] = [...pts.current.values()], d = Math.hypot(a[0] - b[0], a[1] - b[1]);
      const r = stage.current.getBoundingClientRect();
      if (drag.current?.d) zoomAt(d / drag.current.d, (a[0] + b[0]) / 2 - r.left, (a[1] + b[1]) / 2 - r.top);
      drag.current = { ...drag.current, d }; return;
    }
    setT(o => (o.s === 1 ? o : { ...o, x: o.x + e.clientX - prev[0], y: o.y + e.clientY - prev[1] }));
  };
  const up = (e) => { pts.current.delete(e.pointerId); if (pts.current.size < 2 && drag.current) drag.current.d = null; };
  const dbl = (e) => { const r = stage.current.getBoundingClientRect(); if (t.s > 1) reset(); else zoomAt(2.5, e.clientX - r.left, e.clientY - r.top); };
  const imgStyle = base ? `position:absolute;left:${base.left}px;top:${base.top}px;width:${base.w}px;height:${base.h}px;max-width:none;max-height:none;`
    + `transform:translate(${t.x}px,${t.y}px) scale(${t.s});transform-origin:0 0;cursor:${t.s > 1 ? 'grab' : 'zoom-in'};user-select:none;-webkit-user-drag:none`
    : 'position:absolute;visibility:hidden';
  return html`<div class="lightbox viewer" role="dialog" aria-label=${m.name}>
    <div class="vbar">
      <span class="vname" title=${m.name}>${m.caption || m.name}${m.taken_on ? ' · ' + m.taken_on.slice(0, 10) : ''}</span>
      ${items.length > 1 && html`<span class="tiny muted">file ${i + 1} of ${items.length}</span>`}
      ${m.kind === 'pdf' && html`<span class="pager"><button class="btn sm" disabled=${page <= 1} onClick=${() => turn(-1)} aria-label="Previous page">‹</button>
        <span class="small num">page ${page}${pages ? ` of ${pages}` : ''}</span>
        <button class="btn sm" disabled=${!!pages && page >= pages} onClick=${() => turn(1)} aria-label="Next page">›</button></span>`}
      <span style="flex:1"></span>
      ${m.kind !== 'video' && html`<button class="btn sm" onClick=${() => zoomAt(1 / 1.4, ...centre())} aria-label="Zoom out">−</button>
        <span class="small num" style="min-width:42px;text-align:center">${Math.round(t.s * 100)}%</span>
        <button class="btn sm" onClick=${() => zoomAt(1.4, ...centre())} aria-label="Zoom in">+</button>
        <button class="btn sm" onClick=${reset}>Fit</button>`}
      <a class="btn sm" href=${m.url} target="_blank" rel="noopener">Original</a>
      ${onRemove && html`<${ConfirmButton} label="Remove" confirm="Remove from here?" cls="btn sm" onConfirm=${() => onRemove(m)} />`}
      <button class="btn sm" onClick=${onClose}>Close</button></div>
    <div class="vstage" ref=${stage} onWheel=${m.kind === 'video' ? null : onWheel} onDblClick=${dbl}
      onPointerDown=${down} onPointerMove=${move} onPointerUp=${up} onPointerCancel=${up}
      onMouseDown=${(e) => { if (e.target === e.currentTarget && t.s === 1) onClose(); }}>
      ${m.kind === 'video' ? html`<video src=${m.url} controls style="max-width:100%;max-height:100%;margin:auto;display:block" />`
        : html`<img key=${m.id + ':' + page} src=${base ? src : lowSrc} alt=${m.caption || m.name} style=${imgStyle} draggable="false"
          onLoad=${(e) => { if (!base) fit(e.currentTarget); }}
          onError=${() => { if (m.kind === 'pdf' && page > 1 && !m.pages) { setMaxPage(page - 1); setPage(page - 1); setBase(null); } }} />`}
      ${!base && m.kind !== 'video' && html`<div class="muted" style="position:absolute;inset:0;display:grid;place-items:center;pointer-events:none">Loading…</div>`}
    </div>
    ${items.length > 1 && html`<button class="btn nav" style="left:12px" onClick=${() => go(-1)} aria-label="Previous file">‹</button>
      <button class="btn nav" style="right:12px" onClick=${() => go(1)} aria-label="Next file">›</button>`}</div>`;
}

export const fmtDate = (s) => (s ? s.slice(0, 10) : '');
export const fmtDT = (s) => (s ? s.replace('T', ' ').slice(0, 16) : '');
export const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
